import { useRef } from 'react';
import {
  useMotionValue,
  useScroll,
  useVelocity,
  useSpring,
  useTransform,
  useAnimationFrame,
} from 'framer-motion';

/**
 * Modulo wrap di un numero nell'intervallo [min, max).
 * Equivalente a framer-motion's `wrap()` ma inline così evitiamo
 * problemi di compatibilità tra versioni.
 */
const wrap = (min, max, v) => {
  const range = max - min;
  const adjusted = ((((v - min) % range) + range) % range) + min;
  return adjusted;
};

/**
 * Hook per marquee con velocità accoppiata allo scroll.
 *
 * Restituisce un motion value `x` che si può applicare a
 * <motion.div style={{ x }}> dove il contenuto è duplicato
 * almeno una volta in modo che lo scorrimento sia infinito.
 *
 * @param {number} baseVelocity  velocità a riposo, in %/s (negativo = sinistra)
 * @param {object} options
 * @param {number} options.wrapMin             percentuale minima di wrap (es. -50)
 * @param {number} options.wrapMax             percentuale massima di wrap (es. 0)
 * @param {number} options.velocityScale       moltiplicatore della velocità di scroll
 * @param {number} options.scrollVelocityMax   px/s di scroll che mappa al max
 * @param {number} options.spring              spring config per smussare la velocity
 */
const useScrollVelocityX = (
  baseVelocity = -1.5,
  {
    wrapMin = -50,
    wrapMax = 0,
    velocityScale = 3,
    scrollVelocityMax = 1200,
    /* spring più morbido = transizioni di velocità più burrose */
    spring = { damping: 60, stiffness: 220, mass: 1 },
    /* cap del delta per evitare salti dopo un blocco di pagina o un
       cambio di tab — lo scatto avveniva proprio quando RAF restituiva
       delta giganti (es. 200ms) e moveBy esplodeva */
    maxDelta = 40,
  } = {}
) => {
  const baseX = useMotionValue(0);
  const { scrollY } = useScroll();
  const scrollVelocity = useVelocity(scrollY);
  const smoothVelocity = useSpring(scrollVelocity, spring);
  const velocityFactor = useTransform(
    smoothVelocity,
    [0, scrollVelocityMax],
    [0, velocityScale],
    { clamp: false }
  );

  const x = useTransform(baseX, (v) => `${wrap(wrapMin, wrapMax, v)}%`);

  const directionFactor = useRef(1);

  useAnimationFrame((_t, delta) => {
    const dt = Math.min(delta, maxDelta) / 1000;
    let moveBy = directionFactor.current * baseVelocity * dt;

    if (velocityFactor.get() < 0) {
      directionFactor.current = -1;
    } else if (velocityFactor.get() > 0) {
      directionFactor.current = 1;
    }

    moveBy += directionFactor.current * moveBy * velocityFactor.get();
    baseX.set(baseX.get() + moveBy);
  });

  return x;
};

export default useScrollVelocityX;
